import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Shield, Users, Heart, Home, Save, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERMISSION_LABELS = {
  view_own_shifts: 'View Own Shifts',
  view_own_timesheets: 'View Own Timesheets',
  submit_timesheets: 'Submit Timesheets',
  view_messages: 'View Messages',
  send_messages: 'Send Messages',
  view_care_plans: 'View Care Plans',
  log_medications: 'Log Medications',
  report_incidents: 'Report Incidents',
  view_own_care_plan: 'View Own Care Plan',
  view_assigned_caregivers: 'View Assigned Caregivers',
  view_own_shifts: 'View Own Shifts',
  view_medications: 'View Medications',
  view_client_care_plan: 'View Client Care Plan',
  view_client_shifts: 'View Client Shifts',
  view_client_medications: 'View Client Medications',
  view_documents: 'View Documents',
  upload_documents: 'Upload Documents',
  view_analytics: 'View Analytics',
  manage_availability: 'Manage Availability',
  request_leave: 'Request Leave',
  view_payslips: 'View Payslips',
  request_shift_swap: 'Request Shift Swap',
};

const ROLE_CONFIG = {
  caregiver: { icon: Users, label: 'Caregiver', color: 'text-[#00A6A0]', bg: 'bg-[#00A6A0]/10' },
  client: { icon: Heart, label: 'Client', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  family: { icon: Home, label: 'Family Member', color: 'text-amber-500', bg: 'bg-amber-500/10' },
};

const ALL_PERMISSIONS = Object.keys(PERMISSION_LABELS);

export default function PermissionsManagement() {
  const [permissions, setPermissions] = useState([]);
  const [modified, setModified] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => { fetchPermissions(); }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/permissions`, { withCredentials: true });
      setPermissions(data);
      const mods = {};
      data.forEach(p => { mods[p.role] = new Set(p.permissions); });
      setModified(mods);
    } catch { toast.error('Failed to load permissions'); }
    setLoading(false);
  };

  const togglePermission = (role, perm) => {
    setModified(prev => {
      const s = new Set(prev[role] || []);
      if (s.has(perm)) s.delete(perm); else s.add(perm);
      return { ...prev, [role]: s };
    });
  };

  const saveRole = async (role) => {
    setSaving(role);
    try {
      await axios.put(`${API_URL}/api/permissions`, {
        role, permissions: Array.from(modified[role] || [])
      }, { withCredentials: true });
      toast.success(`${ROLE_CONFIG[role]?.label || role} permissions saved`);
    } catch { toast.error('Failed to save permissions'); }
    setSaving(null);
  };

  const resetRole = (role) => {
    const original = permissions.find(p => p.role === role);
    if (original) {
      setModified(prev => ({ ...prev, [role]: new Set(original.permissions) }));
      toast.info('Reset to saved state');
    }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex justify-center py-20"><div className="spinner w-8 h-8" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="permissions-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Permissions Management</h1>
          <p className="text-muted-foreground mt-1">Control what each role can access in your organization</p>
        </div>

        {Object.entries(ROLE_CONFIG).map(([role, config]) => {
          const Icon = config.icon;
          const currentPerms = modified[role] || new Set();
          const originalPerms = new Set(permissions.find(p => p.role === role)?.permissions || []);
          const hasChanges = currentPerms.size !== originalPerms.size || [...currentPerms].some(p => !originalPerms.has(p));

          return (
            <Card key={role} className="glass-card border-border" data-testid={`permissions-${role}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${config.bg}`}>
                      <Icon className={`w-5 h-5 ${config.color}`} />
                    </div>
                    <div>
                      <CardTitle className="text-foreground">{config.label} Permissions</CardTitle>
                      <CardDescription>{currentPerms.size} permission(s) enabled</CardDescription>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {hasChanges && (
                      <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => resetRole(role)} data-testid={`reset-${role}-btn`}>
                        <RotateCcw className="w-4 h-4 mr-1" /> Reset
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white"
                      onClick={() => saveRole(role)}
                      disabled={saving === role}
                      data-testid={`save-${role}-btn`}
                    >
                      <Save className="w-4 h-4 mr-1" /> {saving === role ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {ALL_PERMISSIONS.map(perm => (
                    <label
                      key={perm}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                      data-testid={`perm-toggle-${role}-${perm}`}
                    >
                      <input
                        type="checkbox"
                        checked={currentPerms.has(perm)}
                        onChange={() => togglePermission(role, perm)}
                        className="w-4 h-4 rounded border-border accent-[#00A6A0]"
                      />
                      <span className="text-sm text-foreground">{PERMISSION_LABELS[perm]}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
